import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

// Lo que tarda el dedo en contar como pulsación larga, y lo que dura el
// tooltip a la vista después de soltarlo.
const LONG_PRESS_MS = 450;
const TIP_VISIBLE_MS = 1600;

// Clases enteras, no construidas: Tailwind solo genera las que lee tal cual.
const TIP_POSITION_CLASS = {
    top: 'tooltip-top',
    left: 'tooltip-left',
    right: 'tooltip-right',
};

// Botón icono + etiqueta de la bottom bar de los visores 3D (ModelViewerShell).
//
// En móvil la etiqueta no cabe y solo queda el icono, así que el tooltip
// (`tip`, por defecto la etiqueta) sale también dejando el dedo pulsado, no
// solo con el ratón encima. Esa pulsación larga no cuenta como toque.
const ActionButton = ({ onClick, icon: Icon, label, tip, tipPosition = 'top', isActive = false, isPrimary = false, className = '' }) => {
    const [tipOpen, setTipOpen] = useState(false);
    const pressTimerRef = useRef(null);
    const hideTimerRef = useRef(null);
    const longPressRef = useRef(false);

    useEffect(() => () => {
        clearTimeout(pressTimerRef.current);
        clearTimeout(hideTimerRef.current);
    }, []);

    const handleTouchStart = () => {
        longPressRef.current = false;
        clearTimeout(hideTimerRef.current);
        clearTimeout(pressTimerRef.current);
        pressTimerRef.current = setTimeout(() => {
            longPressRef.current = true;
            setTipOpen(true);
        }, LONG_PRESS_MS);
    };

    // Arrastrar el dedo no es una pulsación larga.
    const cancelPress = () => clearTimeout(pressTimerRef.current);

    const handleTouchEnd = (event) => {
        cancelPress();
        if (!longPressRef.current) return;
        // Sin esto el navegador remata la pulsación larga con un click.
        event.preventDefault();
        hideTimerRef.current = setTimeout(() => setTipOpen(false), TIP_VISIBLE_MS);
    };

    const handleClick = (event) => {
        if (longPressRef.current) {
            longPressRef.current = false;
            event.preventDefault();
            return;
        }
        onClick?.(event);
    };

    const tipText = tip || label;

    return (
        <div
            className={['tooltip', TIP_POSITION_CLASS[tipPosition] || TIP_POSITION_CLASS.top, tipOpen ? 'tooltip-open' : ''].join(' ')}
            data-tip={tipText}
        >
            <button
                onClick={handleClick}
                onTouchStart={handleTouchStart}
                onTouchMove={cancelPress}
                onTouchEnd={handleTouchEnd}
                onTouchCancel={cancelPress}
                // El menú del navegador al mantener pulsado taparía el tooltip.
                onContextMenu={(event) => event.preventDefault()}
                aria-label={tipText}
                className={[
                    'flex flex-col items-center justify-center select-none [-webkit-touch-callout:none]',
                    'min-w-12 sm:min-w-16 px-2 sm:px-3 py-2 rounded-xl',
                    'transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50',
                    isPrimary
                        ? 'bg-base-300 text-base-content shadow-lg hover:bg-neutral active:scale-95'
                        : isActive
                            ? 'bg-base-300/75 text-primary'
                            : 'text-base-content/60 hover:text-base-content hover:bg-base-200',
                    className,
                ].join(' ')}
            >
                <Icon className="w-4 h-4 sm:w-5 sm:h-5 mb-1" />
                <span className="text-xs font-medium whitespace-nowrap hidden sm:block">{label}</span>
            </button>
        </div>
    );
};

ActionButton.propTypes = {
    onClick: PropTypes.func,
    icon: PropTypes.elementType.isRequired,
    label: PropTypes.string.isRequired,
    tip: PropTypes.string,
    tipPosition: PropTypes.oneOf(Object.keys(TIP_POSITION_CLASS)),
    isActive: PropTypes.bool,
    isPrimary: PropTypes.bool,
    className: PropTypes.string,
};

export default ActionButton;
